import { Vector3 } from '@babylonjs/core';
import { GameConfig } from './GameConfig';
import { GrassBuilder, GrassBuilderConfig } from './GrassBuilder';
import { Level } from './Level';
import { GrassConfig } from './LevelConfig';

export class GrassGenerator {
  private grassBuilder: GrassBuilder;

  constructor(public level: Level) {
    this.grassBuilder = new GrassBuilder(this.level.scene, this.level.rng.uFloat32);
  }

  public async generate(config: GrassConfig): Promise<void> {
    if (!config) {
      return;
    }
    this.generateInstances(config);
  }

  public update(deltaTime: number): void {
    this.grassBuilder.update(deltaTime);
  }

  private generateInstances(config: GrassConfig): void {
    const pathPoints = this.level.pathPoints;

    if (!pathPoints || pathPoints.length === 0) return;

    // Iterate efficiently
    const interval = Math.max(1, Math.floor(config.interval));

    const positions: Vector3[] = [];

    for (let i = 0; i < pathPoints.length - 1; i += interval) {
      const point = pathPoints[i];
      const nextPoint = pathPoints[i + 1];
      const nextNextPoint = pathPoints[i + 2];
      const nextNextNextPoint = pathPoints[i + 3];

      // Calculate tangent for orientation
      const tangent = nextPoint.subtract(point).normalize();

      // Calculate normal (lateral) roughly
      const up = Vector3.Up();
      const right = Vector3.Cross(up, tangent).normalize();

      for (let j = 0; j < config.density; j++) {
        // Random placement in zone
        const lateralOffset = this.level.floatInRange(config.zone.min, config.zone.max);

        // Skip dead zone
        if (config.deadZone) {
          if (lateralOffset > config.deadZone.min && lateralOffset < config.deadZone.max) {
            continue;
          }
        }

        // Offset along the path segment slightly (0 to distance to next point)
        const segmentLength = Vector3.Distance(point, nextNextNextPoint ? nextNextNextPoint : nextNextPoint ? nextNextPoint : nextPoint);
        const forwardOffset = this.level.floatInRange(0, segmentLength);

        // Calculate position
        const position = point.add(tangent.scale(forwardOffset)).add(right.scale(lateralOffset));

        // Get ground height
        const groundHeight = this.level.getHeightAtCoordinates(position.x, position.z);
        if (groundHeight !== undefined) {
          position.y = groundHeight;
        }

        positions.push(position);
      }
    }

    if (positions.length > 0) {
      if (GameConfig.logDebug) {
        console.log(`GrassGenerator: Generated ${positions.length} instances.`);
      }
      const grassBuilderConfig: GrassBuilderConfig = {
        bladeHeight: config.bladeHeight,
        bladeWidth: config.bladeWidth,
        windSpeed: config.windSpeed,
        windStrength: config.windStrength,
        baseColor: config.baseColor,
        tipColor: config.tipColor,
        minScale: config.scale.min,
        maxScale: config.scale.max,
        receiveShadows: config.receiveShadows,
        frustumDepth: config.frustumDepth,
        positions: positions,
        impactObjects: [{ position: this.level.player.model.position, radius: 2, strength: 4 }],
      };
      this.grassBuilder.create(grassBuilderConfig);

      if (this.grassBuilder.grassMesh && config.castShadows) {
        this.level.shadowGenerator.addShadowCaster(this.grassBuilder.grassMesh);
      }
    }
  }
}
